import { Link } from "react-router-dom";
import { Shield, Scale, Phone, Mail, Users, FileCheck, AlertTriangle, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Shield,
    title: "Tutela Consumatori",
    description: "Assistenza legale e consulenza per la difesa dei diritti dei consumatori contro pratiche commerciali scorrette."
  },
  {
    icon: Scale,
    title: "Conciliazione",
    description: "Procedure di conciliazione paritetica con aziende e gestori di servizi per risolvere le controversie."
  },
  {
    icon: AlertTriangle,
    title: "Reclami e Contestazioni",
    description: "Supporto nella redazione e invio di reclami formali verso fornitori di beni e servizi."
  },
  {
    icon: FileCheck,
    title: "Contratti e Clausole",
    description: "Verifica di contratti e identificazione di clausole vessatorie o condizioni sfavorevoli."
  },
  {
    icon: Lightbulb,
    title: "Informazione",
    description: "Campagne informative sui diritti dei consumatori e sulle novità normative del settore."
  },
  {
    icon: Users,
    title: "Azioni Collettive",
    description: "Partecipazione a class action e iniziative collettive per la tutela degli interessi dei consumatori."
  }
];

const areas = [
  "Telefonia e telecomunicazioni",
  "Energia elettrica e gas",
  "Servizi bancari e finanziari",
  "Assicurazioni",
  "Trasporti e viaggi",
  "E-commerce e acquisti online",
  "Servizi postali",
  "Pubblica amministrazione"
];

const Udicon = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-blue-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 text-white text-sm font-semibold rounded-full mb-6">
              U.Di.Con - Unione per la Difesa dei Consumatori
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              Difendiamo i tuoi diritti di consumatore
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              U.Di.Con è l'associazione nazionale a tutela dei consumatori. Attraverso il nostro sportello locale, offriamo assistenza e consulenza gratuita per difendere i tuoi diritti.
            </p>
          </div>
        </div>
      </section>

      {/* What is U.Di.Con */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
                Chi Siamo
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                L'Unione per la Difesa dei Consumatori
              </h2>
              <div className="prose prose-lg text-slate-600">
                <p className="mb-6">
                  <strong>U.Di.Con</strong> (Unione per la Difesa dei Consumatori) è un'associazione nazionale riconosciuta dal Ministero dello Sviluppo Economico, nata per tutelare e promuovere i diritti dei cittadini in qualità di consumatori e utenti.
                </p>
                <p className="mb-6">
                  Attraverso il nostro sportello presso il CAF Patronato EPAS di Margherita di Savoia, offriamo:
                </p>
                <ul className="space-y-2 mb-6">
                  <li>Consulenza gratuita su problematiche di consumo</li>
                  <li>Assistenza per reclami e controversie</li>
                  <li>Procedure di conciliazione con aziende</li>
                  <li>Informazione sui diritti dei consumatori</li>
                </ul>
                <p>
                  Il nostro obiettivo è garantire che ogni cittadino possa far valere i propri diritti di fronte a pratiche commerciali scorrette, clausole vessatorie e disservizi.
                </p>
              </div>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">
                Settori di Intervento
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {areas.map((area, idx) => (
                  <div 
                    key={idx}
                    className="bg-white px-4 py-3 rounded-lg text-slate-700 text-sm font-medium"
                  >
                    {area}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
              I Nostri Servizi
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Come possiamo aiutarti
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Offriamo una gamma completa di servizi per la tutela dei tuoi diritti di consumatore.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl border border-slate-200 card-hover"
                data-testid={`udicon-service-${idx}`}
              >
                <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Access */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4 block">
              Come Accedere
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              Sportello U.Di.Con presso il nostro centro
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              Lo sportello U.Di.Con è attivo presso la nostra sede. Puoi richiedere assistenza in diversi modi:
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-50 p-6 rounded-xl">
                <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Di Persona</h3>
                <p className="text-sm text-slate-600">
                  Vieni a trovarci in<br />
                  Corso Nunzio Ricco n° 9
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-xl">
                <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Telefono</h3>
                <p className="text-sm text-slate-600">
                  Chiamaci al<br />
                  <a href="tel:0883411359" className="text-blue-900 font-semibold">0883 411359</a>
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-xl">
                <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">Email</h3>
                <p className="text-sm text-slate-600">
                  Scrivici a<br />
                  <a href="mailto:patronatoepasmargherita@gmail.com" className="text-blue-900 font-semibold text-xs">patronatoepasmargherita@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            Hai un problema come consumatore?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Non restare solo. Prenota un appuntamento gratuito con i nostri esperti U.Di.Con.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prenota">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
                data-testid="udicon-cta-prenota"
              >
                Prenota Consulenza Gratuita
              </Button>
            </Link>
            <Link to="/contatti">
              <Button 
                size="lg" 
                variant="outline"
                className="bg-transparent border-white text-white hover:bg-white/10 rounded-full px-8"
                data-testid="udicon-cta-contatti"
              >
                Contattaci
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Udicon;
